import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { paymentsAPI, billCategoriesAPI, householdAPI } from '../services/api';
import { formatAmount } from '../utils/formatters';
import { parseDate, formatDate, formatDateRelative } from '../utils/dateUtils';
import { useToast } from '../context/ToastContext';
import { Spinner } from '../components/common/LoadingComponents';
import {
  CreditCard,
  AlertCircle,
  CheckCircle,
  Zap,
  TrendingUp,
  ChevronRight,
  UserPlus,
  Check,
  X,
  Clock,
  ArrowUpRight
} from 'lucide-react';
import '../styles/Dashboard.css';
import BillIcon from '../components/common/BillIcon';
import SpendingTrendChart from '../components/common/SpendingTrendChart';

function Dashboard({ user }) {
  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  
  const [payments, setPayments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [upcomingBills, setUpcomingBills] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [notifications, setNotifications] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const isManager = user?.household_role === 'manager';
  const hasHousehold = !!user?.household_id;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [upcomingRes, paymentsRes, categoriesRes, notifRes] = await Promise.all([
        paymentsAPI.getUpcoming(30),
        paymentsAPI.getAll({ limit: 20 }),
        billCategoriesAPI.getAll(false),
        api.get('/notifications?unread=true')
      ]);

      setUpcomingBills(upcomingRes.data);
      setPayments(paymentsRes.data.payments || []);
      setCategories(categoriesRes.data);
      setNotifications(notifRes.data.slice(0, 5));

      if (isManager && hasHousehold) {
        try {
          const requestsRes = await householdAPI.getJoinRequests();
          setJoinRequests(requestsRes.data);
        } catch (e) {
          setJoinRequests([]);
        }
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId, name) => {
    setProcessing(requestId);
    try {
      await householdAPI.processJoinRequest(requestId, 'approve');
      success(`${name} has been added to your household`);
      setJoinRequests(joinRequests.filter(r => r.id !== requestId));
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(requestId);
    try {
      await householdAPI.processJoinRequest(requestId, 'reject');
      success('Join request rejected');
      setJoinRequests(joinRequests.filter(r => r.id !== requestId));
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const getCategoryIcon = (categoryName) => {
    const cat = categories.find(c => c.name === categoryName);
    return cat?.icon || '📋';
  };

  // Derived data
  const overdueBills = upcomingBills.filter(b => b.is_overdue);
  const pendingBills = upcomingBills.filter(b => !b.is_paid && !b.is_overdue);
  const autoPayBills = pendingBills.filter(b => b.auto_pay);
  
  const totalPending = pendingBills.reduce((sum, b) => sum + parseFloat(b.amount), 0);
  const totalOverdue = overdueBills.reduce((sum, b) => sum + parseFloat(b.amount), 0);
  const thisMonthPayments = payments.filter(p => {
    const d = new Date(p.payment_date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const paidThisMonth = thisMonthPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);

  // Category breakdown
  const categoryTotals = {};
  pendingBills.forEach(bill => {
    const cat = bill.category || 'Other';
    categoryTotals[cat] = (categoryTotals[cat] || 0) + parseFloat(bill.amount);
  });
  const topCategories = Object.entries(categoryTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  // Mini calendar
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  
  const billDueDates = new Set();
  const overdueDates = new Set();
  pendingBills.forEach(bill => {
    const d = parseDate(bill.due_date);
    if (d && d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
      billDueDates.add(d.getDate());
    }
  });
  overdueBills.forEach(bill => {
    const d = parseDate(bill.due_date);
    if (d && d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
      overdueDates.add(d.getDate());
    }
  });

  const categoryColors = ['#3b82f6', '#22c55e', '#f97316', '#8b5cf6'];

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-state">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Header */}
      <div className="dashboard-header">
        <h1>Overview</h1>
      </div>

      {/* Overdue Banner - Prominent alert */}
      {overdueBills.length > 0 && (
        <div className="overdue-banner" onClick={() => navigate('/manage#overdue')}>
          <div className="overdue-banner-icon">
            <AlertCircle size={24} />
          </div>
          <div className="overdue-banner-content">
            <strong>{overdueBills.length} Overdue Bill{overdueBills.length > 1 ? 's' : ''}</strong>
            <span>{formatAmount(totalOverdue)} needs attention</span>
          </div>
          <div className="overdue-banner-bills">
            {overdueBills.slice(0, 3).map((bill, idx) => (
              <span key={idx} className="overdue-bill-tag">{bill.bill_name}</span>
            ))}
            {overdueBills.length > 3 && (
              <span className="overdue-bill-tag more">+{overdueBills.length - 3} more</span>
            )}
          </div>
          <button className="btn btn-danger" onClick={(e) => { e.stopPropagation(); navigate('/manage#overdue'); }}>
            Pay Now <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Join Request Alerts */}
      {joinRequests.length > 0 && (
        <div className="alerts-row">
          {joinRequests.map(request => (
            <div key={`request-${request.id}`} className="alert-card info">
              <UserPlus size={20} />
              <div className="alert-content">
                <strong>{request.first_name}</strong>
                <span>wants to join</span>
              </div>
              <div className="alert-actions">
                <button className="btn-sm success" onClick={() => handleApprove(request.id, request.first_name)} disabled={processing === request.id}>
                  <Check size={14} />
                </button>
                <button className="btn-sm danger" onClick={() => handleReject(request.id)} disabled={processing === request.id}>
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats Row */}
      <div className="dashboard-stats">
        <div className="dash-stat-card">
          <div className="dash-stat-icon blue"><Clock size={24} /></div>
          <div className="dash-stat-info">
            <span className="dash-stat-value">{formatAmount(totalPending)}</span>
            <span className="dash-stat-label">Due Soon · {pendingBills.length} bills</span>
          </div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-icon green"><CheckCircle size={24} /></div>
          <div className="dash-stat-info">
            <span className="dash-stat-value success">{formatAmount(paidThisMonth)}</span>
            <span className="dash-stat-label">Paid · {thisMonthPayments.length} this month</span>
          </div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-icon purple"><Zap size={24} /></div>
          <div className="dash-stat-info">
            <span className="dash-stat-value">{autoPayBills.length}</span>
            <span className="dash-stat-label">Auto-pay Bills</span>
          </div>
        </div>
        <div className="dash-stat-card clickable" onClick={() => navigate('/stats')}>
          <div className="dash-stat-icon orange"><TrendingUp size={24} /></div>
          <div className="dash-stat-info">
            <span className="dash-stat-value">{pendingBills.length + overdueBills.length}</span>
            <span className="dash-stat-label">Active Bills</span>
          </div>
        </div>
      </div>

      {/* Spending Trend Chart */}
      <SpendingTrendChart />

      {/* Main Content Grid */}
      <div className="content-grid">
        {/* Left Column */}
        <div className="content-column">
          {/* Upcoming Bills */}
          <div className="card">
            <div className="card-header">
              <h3>Upcoming Bills</h3>
              <Link to="/manage#dueSoon" className="card-link"><ArrowUpRight size={18} /></Link>
            </div>
            <div className="card-body">
              {pendingBills.length === 0 ? (
                <div className="empty-state">
                  <CheckCircle size={32} />
                  <span>All caught up!</span>
                </div>
              ) : (
                <div className="bill-list">
                  {pendingBills.slice(0, 6).map((bill, idx) => (
                    <div key={`${bill.id}-${idx}`} className="bill-item">
                      <BillIcon iconUrl={bill.icon_url} categoryIcon={getCategoryIcon(bill.category)} billName={bill.bill_name} size="sm" />
                      <div className="bill-info">
                        <span className="bill-name">{bill.bill_name}</span>
                        <span className="bill-date">{bill.auto_pay && <Zap size={10} />}{formatDateRelative(bill.due_date)}</span>
                      </div>
                      <span className="bill-amount">{formatAmount(bill.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Payments */}
          <div className="card">
            <div className="card-header">
              <h3>Recent Payments</h3>
              <Link to="/manage#recentPayments" className="card-link"><ArrowUpRight size={18} /></Link>
            </div>
            <div className="card-body">
              {payments.length === 0 ? (
                <div className="empty-state">
                  <CreditCard size={32} />
                  <span>No payments yet</span>
                </div>
              ) : (
                <div className="bill-list">
                  {payments.slice(0, 4).map(payment => (
                    <div key={payment.id} className="bill-item">
                      <BillIcon iconUrl={payment.icon_url} categoryIcon={getCategoryIcon(payment.category)} billName={payment.bill_name} size="sm" />
                      <div className="bill-info">
                        <span className="bill-name">{payment.bill_name}</span>
                        <span className="bill-date">{formatDate(payment.payment_date, { month: 'short', day: 'numeric' })}</span>
                      </div>
                      <span className="bill-amount success">{formatAmount(payment.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="content-column">
          {/* Calendar */}
          <div className="card">
            <div className="card-header">
              <h3>{today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
              <Link to="/calendar" className="card-link"><ArrowUpRight size={18} /></Link>
            </div>
            <div className="card-body">
              <div className="mini-calendar">
                <div className="cal-header">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                    <span key={i} className="cal-day-name">{d}</span>
                  ))}
                </div>
                <div className="cal-days">
                  {[...Array(firstDayOfMonth)].map((_, i) => (
                    <span key={`empty-${i}`} className="cal-day empty"></span>
                  ))}
                  {[...Array(daysInMonth)].map((_, i) => {
                    const day = i + 1;
                    const isToday = day === today.getDate();
                    const hasBill = billDueDates.has(day);
                    const isOverdue = overdueDates.has(day);
                    return (
                      <span key={day} className={`cal-day ${isToday ? 'today' : ''} ${hasBill ? 'has-bill' : ''} ${isOverdue ? 'overdue' : ''}`}>
                        {day}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* By Category */}
          <div className="card">
            <div className="card-header">
              <h3>By Category</h3>
              <Link to="/stats" className="card-link"><ArrowUpRight size={18} /></Link>
            </div>
            <div className="card-body">
              {topCategories.length === 0 ? (
                <div className="empty-state">
                  <TrendingUp size={32} />
                  <span>No data yet</span>
                </div>
              ) : (
                <div className="category-list">
                  {topCategories.map(([cat, amount], idx) => {
                    const catData = categories.find(c => c.name === cat);
                    const percentage = Math.round((amount / totalPending) * 100) || 0;
                    return (
                      <div key={cat} className="category-item">
                        <span className="cat-icon">{catData?.icon || '📋'}</span>
                        <div className="cat-info">
                          <div className="cat-row">
                            <span className="cat-name">{cat}</span>
                            <span className="cat-amount">{formatAmount(amount)}</span>
                          </div>
                          <div className="cat-bar">
                            <div className="cat-bar-fill" style={{ width: `${percentage}%`, backgroundColor: categoryColors[idx] }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
